import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import {
  OrganisationExamProgressDetail,
  OrganisationLessonProgressDetail,
  ProgressScope,
} from '../../types';
import { downloadAssistantContext } from '../../utils/assistantContext';
import { formatTimeAgo } from '../../utils/time';
import MarkdownContent from '../shared/MarkdownContent';

export type ProgressDetail =
  | OrganisationLessonProgressDetail
  | OrganisationExamProgressDetail;

const isLessonProgressDetail = (
  value: ProgressDetail,
): value is OrganisationLessonProgressDetail =>
  value.item.type === 'lesson' && 'chat' in value;

const displayAnswer = (answer: string | string[] | undefined) => {
  if (Array.isArray(answer)) return answer.length ? answer.join(', ') : 'No answer submitted.';
  return answer?.trim() || 'No answer submitted.';
};

const messageLabel = (role: string | null, messageType: string) => {
  if (role === 'user') return 'Student';
  if (role === 'assistant') return 'Tutor';
  return messageType.replace(/_message$/, '').replace(/_/g, ' ');
};

const getSectionCompletion = (
  message: OrganisationLessonProgressDetail['messages'][number],
) => {
  if (
    message.message_type !== 'section_completion_message'
    || !message.additional_data
    || typeof message.additional_data !== 'object'
  ) {
    return null;
  }
  const completion = (message.additional_data as Record<string, unknown>).completion_data;
  if (!completion || typeof completion !== 'object') return null;
  const value = completion as Record<string, unknown>;
  return {
    name: typeof value.section_name === 'string' ? value.section_name : message.content,
    status: typeof value.status === 'string' ? value.status : 'COMPLETED',
  };
};

const classProgressPath = (detail: ProgressDetail, scope?: ProgressScope) => {
  const path = `/organisation/progress/${detail.item.type}/${encodeURIComponent(detail.item.id)}`;
  return scope === undefined ? path : `${path}?class=${scope}`;
};

const StudentProgressDetailModal: React.FC<{
  detail: ProgressDetail;
  scope?: ProgressScope;
  isGlobalAdmin?: boolean;
  onClose: () => void;
}> = ({ detail, scope, isGlobalAdmin = false, onClose }) => {
  const navigate = useNavigate();

  const handleExportContext = async (chatId: string, assistantIndex: number) => {
    try {
      await downloadAssistantContext(chatId, assistantIndex, detail.item.id);
    } catch (e: any) {
      alert(e?.message || 'Failed to download context');
    }
  };

  const handleOpenPromptTester = async (chatId: string, assistantIndex: number) => {
    try {
      const context = await api.getAssistantContext(chatId, assistantIndex);
      navigate('/admin/prompt-tester', { state: { context } });
    } catch (e: any) {
      alert(e?.message || 'Failed to open context in Prompt Tester');
    }
  };

  return (
    <div className="modal-backdrop class-progress-backdrop" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="modal class-progress-modal" role="dialog" aria-modal="true" aria-labelledby="student-progress-title">
        <div className="modal-header">
          <div>
            <h3 id="student-progress-title">
              <Link to={`/manage/user/${detail.student.id}`} onClick={onClose}>
                {detail.student.email}
              </Link>
            </h3>
            <Link className="class-progress-modal-item" to={classProgressPath(detail, scope)} onClick={onClose}>
              {detail.item.title}
            </Link>
          </div>
          <button type="button" className="close" onClick={onClose} aria-label="Close student progress">×</button>
        </div>
        <div className="modal-body class-progress-detail">
          {isLessonProgressDetail(detail) ? (
            <>
              <div className="class-progress-detail-meta">
                <span>{detail.chat.completed_at ? 'Completed' : 'Started'}</span>
                <span>Last active {formatTimeAgo(detail.chat.last_activity_at)}</span>
              </div>
              <section className="class-section-progress" aria-label="Lesson section progress">
                <div className="class-section-progress-heading">
                  <strong>Section progress</strong>
                  <span>
                    {detail.section_progress.completed_count} of {detail.section_progress.total_count} completed
                  </span>
                </div>
                <div
                  className="class-section-progress-track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={detail.section_progress.total_count}
                  aria-valuenow={detail.section_progress.completed_count}
                >
                  <span
                    style={{
                      width: detail.section_progress.total_count > 0
                        ? `${(detail.section_progress.completed_count / detail.section_progress.total_count) * 100}%`
                        : '0%',
                    }}
                  />
                </div>
                {detail.section_progress.sections.length > 0 && (
                  <div className="class-section-progress-list">
                    {detail.section_progress.sections.map((section, index) => (
                      <span
                        key={`${section.name}-${index}`}
                        className={section.completed ? 'is-complete' : ''}
                        title={section.status || 'Not completed'}
                      >
                        {section.completed ? '✓' : index + 1} {section.name}
                      </span>
                    ))}
                  </div>
                )}
              </section>
              <div className="class-transcript">
                {detail.messages.length === 0 ? (
                  <div className="ml-empty">This chat has no saved messages.</div>
                ) : detail.messages.map(message => {
                  const completion = getSectionCompletion(message);
                  if (completion) {
                    return (
                      <article
                        key={message.id ?? `${message.timestamp}-${message.message_type}`}
                        className="class-transcript-section-completion"
                      >
                        <span className="class-transcript-event-icon" aria-hidden="true">✓</span>
                        <div>
                          <strong>{completion.status === 'SKIPPED' ? 'Section skipped' : 'Section completed'}</strong>
                          <span>{completion.name}</span>
                          <small>{completion.status.toLowerCase()}</small>
                        </div>
                        <time>{new Date(message.timestamp).toLocaleString()}</time>
                      </article>
                    );
                  }
                  return (
                    <article
                      key={message.id ?? `${message.timestamp}-${message.message_type}`}
                      className={`class-transcript-message class-transcript-message--${message.role || 'event'}`}
                    >
                      <header>
                        <strong>{messageLabel(message.role, message.message_type)}</strong>
                        <time>{new Date(message.timestamp).toLocaleString()}</time>
                      </header>
                      <MarkdownContent content={message.content || 'No message content.'} />
                      {isGlobalAdmin && message.model_context_available && message.assistant_index !== null && (
                        <div className="class-transcript-context-actions">
                          <button
                            type="button"
                            title="Download context for this message"
                            onClick={() => handleExportContext(detail.chat.id, message.assistant_index as number)}
                          >
                            ⬇️ Context
                          </button>
                          <button
                            type="button"
                            title="Open this message's context in Prompt Tester"
                            onClick={() => handleOpenPromptTester(detail.chat.id, message.assistant_index as number)}
                          >
                            🧪 Prompt Tester
                          </button>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="class-progress-detail-meta">
                <span>{detail.attempt.state.replace('_', ' ')}</span>
                <span>
                  {detail.attempt.obtained_marks !== null && detail.attempt.obtained_marks !== undefined
                    ? `${detail.attempt.obtained_marks} / ${detail.attempt.total_marks ?? '—'} marks`
                    : 'Not marked'}
                </span>
              </div>
              <div className="class-exam-solution">
                {(detail.exam.data.questions || []).map((question, index) => {
                  const result = detail.attempt.marking_data?.questions?.find(
                    value => value.question_index === index,
                  );
                  return (
                    <article key={index}>
                      <header>
                        <strong>Question {index + 1}</strong>
                        <span>{result ? `${result.marks} / ${result.max_marks}` : `${question.marks || 1} marks`}</span>
                      </header>
                      <MarkdownContent content={question.question} />
                      <div className="class-exam-answer">
                        <strong>Student answer</strong>
                        <MarkdownContent content={displayAnswer(detail.attempt.data.answers?.[index])} />
                      </div>
                      {result?.feedback && (
                        <div className="class-exam-feedback">
                          <strong>Feedback</strong>
                          <MarkdownContent content={result.feedback} />
                        </div>
                      )}
                      {question.mark_scheme && (
                        <details>
                          <summary>Mark scheme</summary>
                          <MarkdownContent content={question.mark_scheme} />
                        </details>
                      )}
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentProgressDetailModal;
